import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import objectSupport from 'dayjs/plugin/objectSupport'

import {
  Select,
  DatePicker,
  Form,
  Button,
  Flex,
  Row,
  Col,
  Typography,
  Collapse,
  Tooltip,
  Input,
} from 'antd'
import Loading from '../components/Loading'
import { useUserPlant, useUserTestZones } from '../hooks/User'
import { useMakeModelsByPlant } from '../hooks/MakeModels'
import { useStationInfo } from '../hooks/StationInfo'
import { useShifts } from '../hooks/Shifts'
import { PlantButton } from './PlantButton'
import { setModal } from '../actions/modal-actions'
import { MODAL_SELECT_TESTZONE } from '../constants'
import ExportButton from './Export'
import TracerErrorBoundary from '../components/TracerErrorBoundary'
import { useSelector } from 'react-redux'
import { useFaultTypes } from '../hooks/Faults'
import ShiftSelect, {
  getShiftDateTime,
  getAllShiftsItem,
  getShiftOptions,
} from './ShiftSelect'

dayjs.extend(objectSupport)
dayjs.extend(utc)

const { Option } = Select
const { RangePicker } = DatePicker

const { Text } = Typography

const TestZoneButton = ({ intl }) => {
  const [userTestZones] = useUserTestZones()
  const dispatch = useDispatch()
  const testZoneLabel = intl.formatMessage({
    id: 'faultReport.testZoneLabel',
    defaultMessage: 'Test Zones',
  })

  return (
    <div className="plant testzone">
      {testZoneLabel}:&nbsp;
      <Tooltip
        title={userTestZones.map((testZone) => testZone.description).toString()}
      >
        <Text
          underline
          onClick={(event) => {
            event.stopPropagation()
            dispatch(setModal(MODAL_SELECT_TESTZONE))
          }}
        >
          {userTestZones.length > 0
            ? userTestZones.length + ' selected'
            : 'Select test zones'}
        </Text>
      </Tooltip>
    </div>
  )
}

// Button to select interval (1 min, 5min, 10min) -- pops up modal with options
const IntervalButton = ({ intl, onIntervalChange }) => {
  const [interval, setInterval] = React.useState(60000) // Default to 1 minute

  const handleIntervalChange = (value) => {
    setInterval(value)
    onIntervalChange(value)
  }
  return (
    <div className="interval-button">
      <Text>Refresh Interval:</Text>
      <Select
        value={interval}
        onChange={handleIntervalChange}
        style={{ width: 100, marginLeft: 8 }}
      >
        <Option value={10000}>10 sec</Option>
        <Option value={60000}>1 min</Option>
        <Option value={300000}>5 min</Option>
        <Option value={600000}>10 min</Option>
      </Select>
    </div>
  )
}

export const ReportControl = ({
  intl,
  searchTerm,
  setSearchTerm,
  faults,
  isMonitor,
  setRefreshInterval,
  previousShiftTimes,
}) => {
  const [userPlant] = useUserPlant()

  const { isLoading: makeModelsIsLoading, makeModels: allMakeModels } =
    useMakeModelsByPlant({ plantName: userPlant })
  const { isLoading: shiftsIsLoading, shifts: plantShifts } = useShifts({
    plant_name: userPlant,
  })

  const { isLoading: stationInfoIsLoading, stationInfo: allStationInfo } =
    useStationInfo({ plant_name: userPlant })

  if (stationInfoIsLoading || makeModelsIsLoading || shiftsIsLoading) {
    return <Loading useBackgroundColor={false} />
  }
  return (
    <TracerErrorBoundary>
      <ReportControlForm
        intl={intl}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        faults={faults}
        allStationInfo={allStationInfo}
        allMakeModels={allMakeModels}
        plantShifts={plantShifts}
        isMonitor={isMonitor}
        setRefreshInterval={setRefreshInterval}
        previousShiftTimes={previousShiftTimes}
      />
    </TracerErrorBoundary>
  )
}

export function FilterSummary({ searchTerm, intl }) {
  return (
    <Row id="filter-summary">
      <Col className="filter-summary-item" span={4}>
        Dept: {searchTerm.department || 'none'}
      </Col>
      <Col className="filter-summary-item" span={3}>
        {searchTerm.zones.length} zone{searchTerm.zones.length !== 1 ? 's' : ''}
      </Col>
      <Col className="filter-summary-item" span={3}>
        {searchTerm.teams.length} team{searchTerm.teams.length !== 1 ? 's' : ''}
      </Col>
      <Col className="filter-summary-item" span={4}>
        {searchTerm.stationIds.length} station
        {searchTerm.stationIds.length !== 1 ? 's' : ''}
      </Col>
      <Col className="filter-summary-item" span={4}>
        {searchTerm.makeModelIds.length} vehicle
        {searchTerm.makeModelIds.length !== 1 ? 's' : ''}
      </Col>
    </Row>
  )
}

export function ReportControlForm({
  intl,
  searchTerm,
  setSearchTerm,
  faults,
  allStationInfo,
  allMakeModels,
  plantShifts,
  isMonitor,
  setRefreshInterval,
  previousShiftTimes,
}) {
  const [allDepartments, setAllDepartments] = React.useState([])
  const [allStationIds, setAllStationIds] = React.useState([])
  const [allTeams, setAllTeams] = React.useState([])
  const [allZones, setAllZones] = React.useState([])
  const [availableZones, setAvailableZones] = React.useState(allZones)
  const [availableTeams, setAvailableTeams] = React.useState(allTeams)
  const [manualStationIds, setManualStationIds] = React.useState(
    new Set(searchTerm.stationIds),
  )
  const [form] = Form.useForm()
  const [, setUserTestZones] = useUserTestZones()
  const [activeKey, setActiveKey] = React.useState(['1'])
  const { isLoading: faultTypesLoading, faultTypes: allFaultReasons } =
    useFaultTypes()

  const isCollapsed = () => activeKey.length === 0

  const handleCollapseChange = (key) => {
    setActiveKey(key)
  }

  React.useEffect(() => {
    if (!allStationInfo) {
      return
    }
    setAllStationIds([...new Set(allStationInfo.map((s) => s.station_id))])
    const depts = [
      ...new Set(
        allStationInfo.filter((s) => !!s.department).map((s) => s.department),
      ),
    ]
    setAllDepartments(depts)
    const teams = [
      ...new Set(allStationInfo.filter((s) => !!s.team).map((s) => s.team)),
    ]
    setAllTeams(teams)
    setAvailableTeams(teams)
    const zones = [
      ...new Set(allStationInfo.filter((s) => !!s.zone).map((s) => s.zone)),
    ]
    setAllZones(zones)
    setAvailableZones(zones)
  }, [allStationInfo])

  const todayStart = dayjs().startOf('day')
  const todayEnd = dayjs().endOf('day')

  function isAllSelected(all, selected) {
    return (
      all.length === selected.length &&
      all.every((element, index) => element === selected[index])
    )
  }

  const shifts = getShiftOptions(plantShifts || [])
  const allShiftsItem = getAllShiftsItem()
  const allMakeModelIds = allMakeModels.map((makeModel) => makeModel.id)

  const updateAvailableTeamsAndZones = ({ selectedDepartment }) => {
    if (!selectedDepartment) {
      selectedDepartment = form.getFieldValue('department')
    }
    if (!selectedDepartment) {
      if (availableTeams.length != allTeams.length) {
        setAvailableTeams(allTeams)
      }
      if (availableZones.length != allZones.length) {
        setAvailableZones(allZones)
      }
      return
    }
    const availableStations = allStationInfo.filter(
      (s) => selectedDepartment === s.department,
    )
    setAvailableZones([
      ...new Set(availableStations.filter((s) => !!s.zone).map((s) => s.zone)),
    ])
    setAvailableTeams([
      ...new Set(availableStations.filter((s) => !!s.team).map((s) => s.team)),
    ])
  }

  const updateAvailableTeams = ({ selectedZones }) => {
    if (selectedZones.length === 0) {
      if (availableTeams.length != allTeams.length) {
        setAvailableTeams(allTeams)
      }
      return
    }
    const availableStations = allStationInfo.filter((s) =>
      selectedZones.includes(s.zone),
    )
    setAvailableTeams([
      ...new Set(availableStations.filter((s) => !!s.team).map((s) => s.team)),
    ])
  }

  const updateSelectedStations = ({
    selectedZones,
    selectedTeams,
    selectedDepartment,
  }) => {
    if (!selectedTeams) {
      selectedTeams = form.getFieldValue('teams')
    }
    if (!selectedZones) {
      selectedZones = form.getFieldValue('zones')
    }
    if (!selectedDepartment) {
      selectedDepartment = form.getFieldValue('department')
    }

    if (
      (!selectedZones || selectedZones.length === 0) &&
      (!selectedTeams || selectedTeams.length == 0) &&
      !selectedDepartment
    ) {
      return
    }

    const inSelectedDepartment = (station) => {
      return !selectedDepartment || selectedDepartment === station.department
    }

    const inSelectedZones = (station) => {
      return (
        !selectedZones ||
        selectedZones.length === 0 ||
        selectedZones.includes(station.zone)
      )
    }

    const inSelectedTeams = (station) => {
      return (
        !selectedTeams ||
        selectedTeams.length === 0 ||
        selectedTeams.includes(station.team)
      )
    }

    const stationIds = [
      ...new Set(
        allStationInfo
          .filter(
            (s) =>
              inSelectedDepartment(s) &&
              inSelectedZones(s) &&
              inSelectedTeams(s),
          )
          .map((s) => s.station_id)
          .concat([...manualStationIds]),
      ),
    ]
    form.setFieldValue('stationIds', stationIds)
  }

  // clear out plant-dependent search terms
  // (zone, test zone, department, stations)
  const onPlantSwitch = () => {
    form.setFieldValue('stationIds', [])
    form.setFieldValue('department', null)
    form.setFieldValue('zones', [])
    form.setFieldValue('teams', [])
    setManualStationIds(new Set())
  }

  const onFormClear = () => {
    onPlantSwitch()
    form.setFieldValue('makeModelIds', [])
    form.setFieldValue('shift', Object.keys(shifts)[0])
    form.setFieldValue('period', [todayStart.local(), todayEnd.local()])
  }

  const vinStatusOptions = ['clean', 'passed', 'failed']

  // Sync time-related changes for RepairsByUser real-time synchronization
  const handleFormChange = (changedFields, allFields) => {
    const periodField = allFields.find((f) => f.name[0] === 'period')
    const shiftField = allFields.find((f) => f.name[0] === 'shift')

    if (periodField?.value && shiftField?.value) {
      // Update just the time-related fields for real-time sync
      setSearchTerm({
        ...searchTerm,
        shift: shiftField.value,
        period: {
          start: periodField.value[0],
          end: periodField.value[1],
        },
      })
    }
  }

  const item = [
    {
      key: '1',
      label: (
        <Flex
          vertical={false}
          justify="space-between"
          data-testid="report-form-collapse"
        >
          <Flex vertical={false} gap={30}>
            <PlantButton onPlantSwitch={onPlantSwitch} />
            <TestZoneButton intl={intl} />
            {isMonitor && (
              <IntervalButton
                intl={intl}
                onIntervalChange={(value) => {
                  setRefreshInterval(value)
                }}
              />
            )}
            {isCollapsed() && (
              <FilterSummary searchTerm={searchTerm} intl={intl} />
            )}
          </Flex>
          <ExportButton
            faults={faults}
            intl={intl}
            isMonitor={isMonitor}
            searchTerm={searchTerm}
            previousShiftTimes={previousShiftTimes}
          />
        </Flex>
      ),
      children: (
        <div className="report-control">
          <Form
            layout="vertical"
            form={form}
            onFieldsChange={handleFormChange}
            initialValues={{
              stationIds: searchTerm.stationIds,
              makeModelIds:
                isAllSelected(allMakeModelIds, searchTerm.makeModelIds) === true
                  ? []
                  : searchTerm.makeModelIds,
              shift: searchTerm.shift || allShiftsItem.name,
              period: [
                (searchTerm.period.start && dayjs(searchTerm.period.start)) ||
                  todayStart,
                (searchTerm.period.end && dayjs(searchTerm.period.end)) ||
                  todayEnd,
              ],
              zones: searchTerm.zones,
              department: searchTerm.department,
              teams: searchTerm.teams,
              testZones: searchTerm.testZones,
              reason: searchTerm.reason,
            }}
            onFinish={(values) => {
              const {
                period,
                shift,
                stationIds,
                makeModelIds,
                zones,
                teams,
                department,
                reason,
                vinStatus,
              } = values
              let shiftDateTime = null
              let searchPeriod = null
              if (!!period && !!shift) {
                shiftDateTime = getShiftDateTime({
                  period,
                  currentShiftName: shift,
                  shifts,
                })
                searchPeriod = period
              } else {
                searchPeriod = [todayStart, todayEnd]
                shiftDateTime = {
                  startAt: searchPeriod[0],
                  endAt: searchPeriod[1],
                }
              }
              setSearchTerm({
                start_at: shiftDateTime.startAt,
                end_at: shiftDateTime.endAt,
                stationIds: stationIds,
                makeModelIds:
                  makeModelIds.length === 0 ? allMakeModelIds : makeModelIds,
                zones: zones,
                teams: teams,
                department: department,
                shift,
                period: { start: searchPeriod[0], end: searchPeriod[1] },
                reason: reason,
                vinStatus: vinStatus,
              })
            }}
          >
            <Form.Item
              label={intl.formatMessage({
                id: 'faultReport.filters.departments.label',
              })}
              name="department"
            >
              <Select
                name="department"
                allowClear
                placeholder={intl.formatMessage({
                  id: 'faultReport.filters.departments.placeholder',
                  defaultMessage: 'All Departments',
                })}
                onChange={(selectedDepartment) => {
                  updateAvailableTeamsAndZones({ selectedDepartment })
                  updateSelectedStations({ selectedDepartment })
                }}
              >
                {allDepartments.map((dept) => (
                  <Option key={dept} value={dept}>
                    {dept}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label={intl.formatMessage({
                id: 'faultReport.filters.zones.label',
              })}
              name="zones"
            >
              <Select
                name="zones"
                mode="multiple"
                allowClear
                placeholder={intl.formatMessage({
                  id: 'faultReport.filters.zones.placeholder',
                  defaultMessage: 'All Zones',
                })}
                onChange={(selectedZones) => {
                  updateAvailableTeams({ selectedZones })
                  updateSelectedStations({ selectedZones })
                }}
              >
                {availableZones.map((zone) => (
                  <Option key={zone} value={zone}>
                    {zone}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label={intl.formatMessage({
                id: 'faultReport.filters.teams.label',
              })}
              name="teams"
            >
              <Select
                name="teams"
                mode="multiple"
                allowClear
                placeholder={intl.formatMessage({
                  id: 'faultReport.filters.teams.placeholder',
                  defaultMessage: 'All Teams',
                })}
                onChange={(selectedTeams) => {
                  updateSelectedStations({ selectedTeams })
                }}
              >
                {availableTeams.map((team) => (
                  <Option key={team} value={team}>
                    {team}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label={intl.formatMessage({
                id: 'faultReport.filters.stations.label',
              })}
              name="stationIds"
            >
              <Select
                name="stationIds"
                mode="multiple"
                allowClear
                placeholder={intl.formatMessage({
                  id: 'faultReport.filters.stations.placeholder',
                })}
                onSelect={(value) => {
                  setManualStationIds((prev) => new Set([...prev, value]))
                }}
                onDeselect={(value) => {
                  setManualStationIds((prev) => {
                    const next = new Set(prev)
                    next.delete(value)
                    return next
                  })
                }}
                onClear={() => setManualStationIds(new Set())}
              >
                {allStationIds.map((station) => (
                  <Option key={station} value={station}>
                    {station}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label={intl.formatMessage({
                id: 'faultReport.filters.vehicles.label',
              })}
              name="makeModelIds"
            >
              <Select
                name="makeModelIds"
                mode="multiple"
                allowClear
                placeholder={intl.formatMessage({
                  id: 'faultReport.filters.vehicles.placeholder',
                })}
              >
                {allMakeModels.map((makeModel) => (
                  <Option key={makeModel.id} value={makeModel.id}>
                    {`${makeModel.make} - ${makeModel.model} - ${makeModel.year} - ${makeModel.phase}`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {!isMonitor && (
              <Form.Item dependencies={['period']}>
                {({ getFieldValue }) => (
                  <Form.Item
                    name="shift"
                    label={intl.formatMessage({
                      id: 'faultReport.filters.shifts.placeholder',
                    })}
                  >
                    <ShiftSelect
                      disabled={
                        getFieldValue('period')[0].diff(
                          getFieldValue('period')[1],
                          'days',
                        ) !== 0
                      }
                    />
                  </Form.Item>
                )}
              </Form.Item>
            )}
            {!isMonitor && (
              <Form.Item
                label={intl.formatMessage({
                  id: 'faultReport.filters.shifts.period',
                })}
                name="period"
                layout="horizontal"
              >
                <RangePicker
                  name="period"
                  data-cy="report-date-range"
                  allowClear={false}
                  onChange={(period) => {
                    if (!period[0].isSame(period[1])) {
                      form.setFieldValue('shift', allShiftsItem.name)
                    }
                  }}
                  presets={[
                    {
                      label: 'Today',
                      value: [todayStart.local(), todayEnd.local()],
                    },
                  ]}
                />
              </Form.Item>
            )}
            {isMonitor && (
              <>
                <Form.Item
                  label={intl.formatMessage({
                    id: 'faultReport.filters.reason',
                    defaultMessage: 'Reason',
                  })}
                  name="reason"
                >
                  <Select name="reason" placeholder="Fault Reason" allowClear>
                    {Array.isArray(allFaultReasons) &&
                      allFaultReasons.map((reason) => (
                        <Option key={reason} value={reason}>
                          {reason}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label={intl.formatMessage({
                    id: 'faultReport.filters.vinStatus',
                    defaultMessage: 'VIN Status',
                  })}
                  name="vinStatus"
                >
                  <Select name="vinStatus" placeholder="VIN Status" allowClear>
                    {vinStatusOptions.map((status) => (
                      <Option key={status} value={status}>
                        {status}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}
            <Flex vertical={false} justify="space-between">
              <Button
                htmlType="submit"
                type="primary"
                onClick={() => {
                  setActiveKey([])
                }}
              >
                {intl.formatMessage({ id: 'forms.submit' })}
              </Button>
              <Button onClick={onFormClear}>
                {intl.formatMessage({ id: 'forms.clearButton' })}
              </Button>
            </Flex>
          </Form>
        </div>
      ),
    },
  ]

  return (
    <Collapse
      onChange={handleCollapseChange}
      items={item}
      defaultActiveKey={['1']}
      activeKey={activeKey}
    />
  )
}
